import { useQuery } from '@tanstack/react-query';

import { getBookingFees } from '../api/endpoints/booking-fees';
import { insideNativeApp } from '../auth/native-app';
import { isOwner } from '../auth/roles';
import { useSession } from '../auth/useSession';
import { formatMoneyCents } from '../lib/format';
import { formatTrialDate } from '../lib/trial';
import { useBusiness } from '../lib/useBusiness';

const FEES_STALE_MS = 5 * 60 * 1000;

/**
 * Independientes pays per booking from the app (Martín, 2026-11-02). Only its
 * owner reads what is owed, and never inside the iPhone app, where Apple does
 * not let a free companion app point to a way to pay.
 */
function useBookingFees() {
  const { session } = useSession();
  const business = useBusiness();
  const enabled = isOwner(session?.role) && business?.plan === 'reservas' && !insideNativeApp(session);
  const query = useQuery({ queryKey: ['booking-fees'], queryFn: getBookingFees, enabled, staleTime: FEES_STALE_MS });
  return enabled ? query.data : undefined;
}

/**
 * Above the work while an invoice is open: how much, by when, and Stripe's own
 * page to pay it. Past the date it says plainly that the business is out of
 * the app until it pays.
 */
export function BookingFeesNotice() {
  const fees = useBookingFees();
  const invoice = fees?.billed ? fees.invoice : null;
  if (!invoice) return null;
  const amount = formatMoneyCents(invoice.amountCents);
  if (invoice.overdue) {
    return (
      <p role="alert" className="notice fee-notice" data-tone="due">
        Tu negocio no sale en la app de Dale hasta que pagues {amount} de tus reservas.{' '}
        <a href={invoice.payUrl} target="_blank" rel="noopener noreferrer">Pagar ahora</a>
      </p>
    );
  }
  return (
    <p role="status" className="notice fee-notice">
      Tienes {amount} por pagar de tus reservas en la app. Paga antes del {formatTrialDate(invoice.dueAt)} para seguir
      saliendo en Dale. <a href={invoice.payUrl} target="_blank" rel="noopener noreferrer">Pagar</a>
    </p>
  );
}

function bookingsLine(count: number): string {
  return `${count} ${count === 1 ? 'reserva' : 'reservas'} desde la app este mes`;
}

/** On Reservas: this month so far, what is free, and what waits for the next invoice. */
export function BookingFeesMonth() {
  const fees = useBookingFees();
  if (!fees?.billed) return null;
  const { thisMonth } = fees;
  return (
    <section className="section" aria-label="Tu cuenta con Dale">
      <h2 className="section-label">Tu cuenta con Dale</h2>
      <div className="card fee-month">
        <div className="fee-month-head">
          <p className="row-title grow">{bookingsLine(thisMonth.bookings)}</p>
          <p className="amount">{thisMonth.amountCents > 0 ? formatMoneyCents(thisMonth.amountCents) : 'Gratis'}</p>
        </div>
        <p className="row-sub">
          Las primeras {fees.freePerMonth} de cada mes son gratis y cada una después cuesta {formatMoneyCents(fees.unitCents)}.
        </p>
        {fees.pendingCents > 0 ? (
          <p className="row-sub">
            Quedan {formatMoneyCents(fees.pendingCents)} de meses anteriores. Se cobran cuando sumen {formatMoneyCents(fees.minimumCents)}.
          </p>
        ) : null}
      </div>
    </section>
  );
}
